import React, { useEffect, useRef, useState } from "react";
import "./LandingPage.css";
import { getLandingStats } from "../../services/landingService";

export default function LandingPage() {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const statVehicles = useRef(null);
  const statDrivers = useRef(null);
  const statSuppliers = useRef(null);
  const statDocs = useRef(null);
  const barVehicles = useRef(null);
  const barDrivers = useRef(null);

  useEffect(() => {
    document.title =
      "Srinivasa Fleet Management Pro — Run a tighter fleet";

    let mounted = true;

    const loadLandingStats = async () => {
      try {
        setStatsLoading(true);

        const data = await getLandingStats();

        if (mounted) {
          setStats(data);
        }
      } catch (error) {
        console.error(
          "Failed to load landing statistics:",
          error
        );
      } finally {
        if (mounted) {
          setStatsLoading(false);
        }
      }
    };

    loadLandingStats();

    const revealEls =
      document.querySelectorAll(".sf-reveal");

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    revealEls.forEach((el) => io.observe(el));

    return () => {
      mounted = false;
      io.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!stats) {
      return;
    }

    const countTo = (
      el,
      target,
      suffix = "",
      duration = 1200
    ) => {
      if (!el) {
        return;
      }

      const numericTarget = Number(target) || 0;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min(
          1,
          (now - start) / duration
        );

        const eased =
          1 - Math.pow(1 - progress, 3);

        const value = Math.round(
          numericTarget * eased
        );

        el.innerHTML = `${value}${suffix}`;

        if (progress < 1) {
          requestAnimationFrame(tick);
        }
      };

      requestAnimationFrame(tick);
    };

    const vehiclesTotal =
      Number(stats.vehicles_total) || 0;

    const vehiclesOnRoad =
      Number(stats.vehicles_on_road) || 0;

    const driversTotal =
      Number(stats.drivers_total) || 0;

    const driversOnDuty =
      Number(stats.drivers_on_duty) || 0;

    const suppliersTotal =
      Number(stats.suppliers_total) || 0;

    const documentsTotal =
      Number(stats.documents_total) || 0;

    const documentsExpiring =
      Number(stats.documents_expiring_30_days) || 0;

    const timer = setTimeout(() => {
      countTo(
        statVehicles.current,
        vehiclesOnRoad,
        `<span class="sf-unit">/ ${vehiclesTotal}</span>`,
        1400
      );

      countTo(
        statDrivers.current,
        driversOnDuty,
        `<span class="sf-unit">/ ${driversTotal}</span>`,
        1400
      );

      countTo(
        statSuppliers.current,
        suppliersTotal,
        "",
        1200
      );

      countTo(
        statDocs.current,
        documentsExpiring,
        "",
        1000
      );

      if (barVehicles.current) {
        const vehiclePercentage =
          vehiclesTotal > 0
            ? Math.min(
                100,
                (vehiclesOnRoad / vehiclesTotal) * 100
              )
            : 0;

        barVehicles.current.style.width =
          `${vehiclePercentage}%`;
      }

      if (barDrivers.current) {
        const driverPercentage =
          driversTotal > 0
            ? Math.min(
                100,
                (driversOnDuty / driversTotal) * 100
              )
            : 0;

        barDrivers.current.style.width =
          `${driverPercentage}%`;
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [stats]);

  const vehiclesTotal =
    Number(stats?.vehicles_total) || 0;

  const vehiclesOnRoad =
    Number(stats?.vehicles_on_road) || 0;

  const driversTotal =
    Number(stats?.drivers_total) || 0;

  const driversOnDuty =
    Number(stats?.drivers_on_duty) || 0;

  const suppliersTotal =
    Number(stats?.suppliers_total) || 0;

  const documentsTotal =
    Number(stats?.documents_total) || 0;

  const documentsExpiring =
    Number(stats?.documents_expiring_30_days) || 0;

  return (
    <div className="sf-landing">
      <nav className="sf-nav">
        <div className="sf-nav-inner">
          <a href="/" className="sf-brand">
            <div className="sf-brand-mark">
              <img
                src="/assets/logo.jpg"
                alt="Srinivasa Fleet Management Pro"
              />
            </div>

            <div className="sf-brand-name">
              SRINIVASA FLEET{" "}
              <span>MANAGEMENT PRO</span>
            </div>
          </a>

          <div className="sf-nav-links">
            <a href="/">Home</a>
            <a href="#features">Features</a>
            <a href="#product">Product</a>
            <a href="#contact">Contact</a>

            <a
              className="sf-nav-cta"
              href="/login"
            >
              Log in
            </a>
          </div>
        </div>
      </nav>

      <header className="sf-hero">
        <div className="sf-wrap sf-hero-grid">
          <div>
            <div className="sf-eyebrow">
              <span className="sf-dot" /> Live fleet
              operations board
            </div>

            <h1>
              Run every vehicle, driver
              <br /> and document from{" "}
              <span className="sf-accent">
                one dispatch board.
              </span>
            </h1>

            <p className="sf-lead">
              Srinivasa Fleet Management Pro
              replaces spreadsheets and paper files
              with a single system for GPS tracking,
              driver records, vehicle documents,
              supplier ledgers, and reports — built
              for Indian fleet operators.
            </p>

            <div className="sf-hero-actions">
              <a
                href="#contact"
                className="sf-btn sf-btn-primary"
              >
                Request a demo →
              </a>

              <a
                href="/login"
                className="sf-btn sf-btn-ghost"
              >
                Log in
              </a>
            </div>

            <div className="sf-hero-microcopy">
              <div>
                <CheckIcon /> Live GPS tracking
              </div>

              <div>
                <CheckIcon /> Document expiry alerts
              </div>

              <div>
                <CheckIcon /> Built for Indian fleets
              </div>
            </div>
          </div>

          <div
            className="sf-board"
            role="img"
            aria-label="Live fleet dispatch board showing vehicles, drivers, suppliers and documents"
          >
            <div className="sf-board-head">
              <div className="sf-board-head-title">
                Fleet Ops · Hyderabad
              </div>

              <div className="sf-board-live">
                <span className="sf-dot" /> LIVE
              </div>
            </div>

            <div className="sf-board-rows">
              <div>
                <div className="sf-board-row">
                  <div className="sf-board-row-label">
                    Vehicles on road
                  </div>

                  <div
                    className="sf-board-row-value"
                    ref={statVehicles}
                  >
                    {statsLoading
                      ? "..."
                      : `0`}
                    <span className="sf-unit">
                      / {vehiclesTotal}
                    </span>
                  </div>
                </div>

                <div className="sf-board-bar">
                  <div
                    className="sf-board-bar-fill"
                    ref={barVehicles}
                    style={{ width: 0 }}
                  />
                </div>
              </div>

              <div>
                <div className="sf-board-row">
                  <div className="sf-board-row-label">
                    Drivers on duty
                  </div>

                  <div
                    className="sf-board-row-value"
                    ref={statDrivers}
                  >
                    {statsLoading
                      ? "..."
                      : `0`}
                    <span className="sf-unit">
                      / {driversTotal}
                    </span>
                  </div>
                </div>

                <div className="sf-board-bar">
                  <div
                    className="sf-board-bar-fill"
                    ref={barDrivers}
                    style={{ width: 0 }}
                  />
                </div>
              </div>

              <div className="sf-board-divider" />

              <div className="sf-board-row">
                <div className="sf-board-row-label">
                  Active suppliers
                </div>

                <div
                  className="sf-board-row-value"
                  ref={statSuppliers}
                >
                  {statsLoading ? "..." : 0}
                </div>
              </div>

              <div className="sf-board-row">
                <div className="sf-board-row-label">
                  Documents expiring in 30 days
                </div>

                <div
                  className="sf-board-row-value"
                  ref={statDocs}
                >
                  {statsLoading ? "..." : 0}
                </div>
              </div>
            </div>

            <div className="sf-board-footer">
              <span>
                {statsLoading
                  ? "Loading live data..."
                  : `${documentsTotal.toLocaleString()} documents monitored`}
              </span>

              <span>
                Zone: Adibatla · Hyderabad
              </span>
            </div>
          </div>
        </div>
      </header>

      <div style={{ position: "relative" }}>
        <div className="sf-spine" />

        <section
          className="sf-features sf-section-pad"
          id="features"
        >
          <div className="sf-wrap">
            <div className="sf-section-head sf-reveal">
              <p className="sf-kicker">
                What's on the board
              </p>

              <h2 className="sf-section-title">
                Six modules, one system of record.
              </h2>

              <p className="sf-section-sub">
                Every module writes to the same fleet
                record, so a vehicle's documents,
                assignments, and supplier history are
                never more than one click away.
              </p>
            </div>

            <div className="sf-feature-grid sf-reveal">
              <FeatureCard
                route="RT · GPS"
                title="GPS Tracking"
              >
                Watch every vehicle move in real time,
                replay routes, and get alerts the moment
                a vehicle leaves its zone.
              </FeatureCard>

              <FeatureCard
                route="RT · DRV"
                title="Driver Management"
              >
                Licenses, contact details, assignments
                and history for every driver — searchable
                in seconds, not folders.
              </FeatureCard>

              <FeatureCard
                route="RT · VEH"
                title="Vehicle Management"
              >
                Registration, insurance, permits and
                fitness certificates tracked with
                automatic expiry reminders.
              </FeatureCard>

              <FeatureCard
                route="RT · SUP"
                title="Supplier Management"
              >
                Keep supplier contracts, capacity, and
                outstanding balances in one ledger
                instead of scattered spreadsheets.
              </FeatureCard>

              <FeatureCard
                route="RT · DSH"
                title="Dashboard"
              >
                One screen for fleet health — active
                vehicles, expiring documents, and open
                assignments at a glance.
              </FeatureCard>

              <FeatureCard
                route="RT · RPT"
                title="Reports"
              >
                Driver, vehicle, supplier and outstanding
                reports, ready to export whenever
                ownership asks for numbers.
              </FeatureCard>
            </div>
          </div>
        </section>

        <section className="sf-about sf-section-pad" id="about">
          <div className="sf-wrap sf-about-grid sf-reveal">
            <div>
              <p className="sf-kicker">About us</p>

              <h2 className="sf-section-title">
                Built by people who run fleets, not just
                software.
              </h2>

              <p className="sf-about-text">
                Srinivasa Fleet Management Pro started
                as an internal tool for Srinivasa Tours
                &amp; Travels, built to replace the
                registers, WhatsApp reminders, and
                scattered spreadsheets that most
                transport operators still rely on.
                After it held up under our own daily
                dispatch load, we opened it up for other
                fleet operators to use.
              </p>

              <p className="sf-about-text">
                Today it's built for tours &amp; travels
                operators, logistics companies, and
                transport contractors who need one place
                to track vehicles, drivers, documents,
                and suppliers — without hiring a software
                team to maintain it.
              </p>

             
            </div>

            <div className="sf-about-audience">
              <h4>Who it's for</h4>

              <ul>
                <li>Tours &amp; travels operators</li>
                <li>Logistics &amp; freight companies</li>
                <li>Transport contractors</li>
                <li>Multi-branch fleet operators</li>
                <li>Vehicle leasing businesses</li>
              </ul>
            </div>
          </div>
        </section>

        <section
          className="sf-why sf-section-pad"
          id="why-us"
        >
          <div className="sf-wrap">
            <div className="sf-section-head sf-reveal">
              <p className="sf-kicker">
                Why choose us
              </p>

              <h2 className="sf-section-title">
                Everything a dispatch desk actually needs.
              </h2>
            </div>

            <div className="sf-why-grid sf-reveal">
              <WhyItem title="Live GPS tracking">
                Know where every vehicle is, right now,
                without calling the driver.
              </WhyItem>

              <WhyItem title="Document expiry alerts">
                Insurance, permits and fitness
                certificates flagged before they lapse.
              </WhyItem>

              <WhyItem title="Secure cloud platform">
                Your fleet data backed up and accessible
                from anywhere, not one office PC.
              </WhyItem>

              <WhyItem title="Real-time notifications">
                Chat, assignment, and document alerts
                land the moment they happen.
              </WhyItem>

              <WhyItem title="Fast dashboard">
                Built for daily use — loads quickly even
                with hundreds of vehicles.
              </WhyItem>

              <WhyItem title="Multi-branch support">
                Manage vehicles and drivers across
                multiple service locations from one
                login.
              </WhyItem>

              <WhyItem title="Mobile friendly">
                Check fleet status from a phone in the
                yard, not just a desktop.
              </WhyItem>

              <WhyItem title="Role-based access">
                Staff see only what their role needs —
                admin, dispatcher, or accounts.
              </WhyItem>
            </div>
          </div>
        </section>

        <section
          className="sf-showcase sf-section-pad"
          id="product"
        >
          <div className="sf-wrap">
            <div className="sf-section-head sf-reveal">
              <p className="sf-kicker sf-kicker-light">
                Inside the board
              </p>

              <h2 className="sf-section-title">
                A dashboard your dispatch team will
                actually open every morning.
              </h2>

              <p className="sf-section-sub sf-section-sub-light">
                Clean, fast, and built around how fleet
                teams actually work — not a generic admin
                panel.
              </p>
            </div>

            <div className="sf-browser-frame sf-reveal">
              <div className="sf-browser-bar">
                <div className="sf-browser-dot" />
                <div className="sf-browser-dot" />
                <div className="sf-browser-dot" />

                <div className="sf-browser-url">
                  app.srinivasafleet.com/dashboard
                </div>
              </div>

              <div className="sf-mock-body">
                <div className="sf-mock-sidebar">
                  <div
                    className="sf-mock-pill sf-active"
                    style={{ width: "70%" }}
                  />

                  <div
                    className="sf-mock-pill"
                    style={{ width: "85%" }}
                  />

                  <div
                    className="sf-mock-pill"
                    style={{ width: "60%" }}
                  />

                  <div
                    className="sf-mock-pill"
                    style={{ width: "75%" }}
                  />

                  <div
                    className="sf-mock-pill"
                    style={{ width: "55%" }}
                  />

                  <div
                    className="sf-mock-pill"
                    style={{ width: "80%" }}
                  />
                </div>

                <div className="sf-mock-cards">
                  <div className="sf-mock-stat">
                    <div className="sf-mono">
                      {statsLoading
                        ? "—"
                        : vehiclesTotal}
                    </div>

                    <div className="sf-lbl">
                      ACTIVE VEHICLES
                    </div>
                  </div>

                  <div className="sf-mock-stat">
                    <div className="sf-mono">
                      {statsLoading
                        ? "—"
                        : driversOnDuty}
                    </div>

                    <div className="sf-lbl">
                      DRIVERS ON DUTY
                    </div>
                  </div>

                  <div className="sf-mock-stat">
                    <div className="sf-mono">
                      {statsLoading
                        ? "—"
                        : suppliersTotal}
                    </div>

                    <div className="sf-lbl">
                      SUPPLIERS
                    </div>
                  </div>

                  <div className="sf-mock-stat">
                    <div className="sf-mono">
                      {statsLoading
                        ? "—"
                        : documentsExpiring}
                    </div>

                    <div className="sf-lbl">
                      DOCS EXPIRING
                    </div>
                  </div>

                  <div className="sf-mock-row">
                    {[
                      40,
                      65,
                      50,
                      80,
                      35,
                      70,
                      55,
                      90,
                      45,
                      60,
                    ].map((h, i) => (
                      <div
                        key={i}
                        className="sf-mock-bar"
                        style={{
                          height: `${h}%`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="sf-stats-band sf-section-pad">
          <div className="sf-wrap">
            <div className="sf-stats-grid sf-reveal">
              <StatBlock
                num={
                  statsLoading
                    ? "—"
                    : vehiclesTotal.toLocaleString()
                }
                label="Vehicles tracked across service locations"
              />

              <StatBlock
                num={
                  statsLoading
                    ? "—"
                    : driversTotal.toLocaleString()
                }
                label="Drivers managed with full document history"
              />

              <StatBlock
                num={
                  statsLoading
                    ? "—"
                    : suppliersTotal.toLocaleString()
                }
                label="Active suppliers managed across the fleet"
              />

              <StatBlock
                num={
                  statsLoading
                    ? "—"
                    : documentsTotal.toLocaleString()
                }
                label="Vehicle documents stored and monitored"
              />
            </div>

            <p className="sf-stats-disclaimer">
              Live figures loaded from the fleet management
              system.
            </p>
          </div>
        </section>

        <section className="sf-testimonials sf-section-pad">
          <div className="sf-wrap">
            <div className="sf-section-head sf-reveal">
              <p className="sf-kicker">
                What operators say
              </p>

              <h2 className="sf-section-title">
                Testimonials coming soon.
              </h2>

              <p className="sf-section-sub">
                We're collecting feedback from early
                fleet operators using the platform. Real
                testimonials will appear here shortly.
              </p>
            </div>
          </div>
        </section>

        <section
          className="sf-seo sf-section-pad"
          id="solutions"
        >
          <div className="sf-wrap">
            <div className="sf-section-head sf-reveal">
              <p className="sf-kicker">Solutions</p>

              <h2 className="sf-section-title">
                Fleet management software built for how
                Indian operators work.
              </h2>
            </div>

            <div className="sf-seo-grid sf-reveal">
              <SeoBlock title="Fleet Management Software">
                A single system to manage vehicles,
                drivers, suppliers, and documents —
                replacing registers and spreadsheets with
                one dispatch board your whole team can
                see.
              </SeoBlock>

              <SeoBlock title="GPS Vehicle Tracking">
                Track vehicle location and movement in
                real time, so dispatch decisions don't
                depend on a phone call to the driver.
              </SeoBlock>

              <SeoBlock title="Driver Management System">
                Store license details, contact information,
                assignment history, and documents for every
                driver in one searchable record.
              </SeoBlock>

              <SeoBlock title="Vehicle Document Management">
                Insurance, permits, fitness certificates
                and registration papers, with automatic
                reminders before anything expires.
              </SeoBlock>

              <SeoBlock title="Fleet Dashboard">
                A live overview of active vehicles,
                drivers on duty, suppliers, and documents
                nearing expiry — the first screen your
                dispatch team opens every morning.
              </SeoBlock>
            </div>
          </div>
        </section>

        <section
          className="sf-cta-band sf-section-pad"
          id="contact"
        >
          <div className="sf-wrap">
            <div className="sf-cta-card sf-reveal">
              <h2>Ready to run a tighter fleet?</h2>

              <p>
                Tell us about your fleet size and we'll
                set up your dispatch board — usually
                within a day.
              </p>

              <div className="sf-cta-actions">
                <a
                  className="sf-btn sf-btn-primary"
                  href="mailto:hello@srinivasafleet.com"
                >
                  Email us →
                </a>

                <a
                  className="sf-btn sf-btn-ghost"
                  href="tel:+918247582810"
                >
                  Call us
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer className="sf-footer">
        <div className="sf-wrap">
          <div className="sf-footer-grid">
            <div className="sf-footer-col sf-footer-brand">
              <a
                href="/"
                className="sf-brand"
                style={{ marginBottom: 16 }}
              >
                <div className="sf-brand-mark">
                  <img
                    src="/assets/logo.jpg"
                    alt="Srinivasa Fleet Management Pro"
                  />
                </div>

                <div className="sf-brand-name">
                  SRINIVASA FLEET{" "}
                  <span>MANAGEMENT PRO</span>
                </div>
              </a>

              <p>
                Fleet management software for GPS
                tracking, driver records, vehicle
                documents, and supplier ledgers — built
                for operators in Hyderabad and beyond.
              </p>
            </div>

            <div className="sf-footer-col">
              <h4>Product</h4>
              <a href="/">Home</a>
              <a href="#features">Features</a>
              <a href="#product">Dashboard</a>
              <a href="/login">Management Log in</a>
              <a href="/supplier/login">Supplier Portal</a>
            </div>

            <div className="sf-footer-col">
              <h4>Solutions</h4>
              <a href="/login">
                GPS Vehicle Tracking
              </a>
              <a href="/login">
                Driver Management
              </a>
              <a href="/login">
                Supplier Management
              </a>
              <a href="/login">
                Reports &amp; Dashboard
              </a>
            </div>

            <div className="sf-footer-col">
              <h4>Contact</h4>

              <a href="mailto:rajashekarmaddi562@gmail.com">
                rajashekarmaddi562@gmail.com
              </a>

              <a href="tel:+918247582810">
                +91 82475 82810
              </a>

              <span className="sf-footer-address">
                Hyderabad, Telangana
              </span>

              <a
                href="https://www.linkedin.com/in/maddi-rajashekar-571a09276/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Maddi Rajashekar · LinkedIn
              </a>
            </div>
          </div>

          <div className="sf-footer-bottom">
            <span>
              © {new Date().getFullYear()} Srinivasa
              Fleet Management Pro. All rights reserved.
              Developed by{" "}
              <a
                href="https://www.linkedin.com/in/maddi-rajashekar-571a09276/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Maddi Rajashekar
              </a>
            </span>

            <div className="sf-social-row">
              <a
                href="https://www.linkedin.com/in/maddi-rajashekar-571a09276/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn - Maddi Rajashekar"
                title="Maddi Rajashekar on LinkedIn"
              >
                <LinkedInIcon />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ route, title, children }) {
  return (
    <div className="sf-feature-card">
      <span className="sf-feature-route sf-mono">
        {route}
      </span>

      <h3>{title}</h3>

      <p>{children}</p>
    </div>
  );
}

function WhyItem({ title, children }) {
  return (
    <div className="sf-why-item">
      <h3>{title}</h3>

      <p>{children}</p>
    </div>
  );
}

function SeoBlock({ title, children }) {
  return (
    <div className="sf-seo-block">
      <h3>{title}</h3>

      <p>{children}</p>
    </div>
  );
}

function StatBlock({ num, label }) {
  return (
    <div className="sf-stat-block">
      <div className="sf-stat-num">{num}</div>

      <div className="sf-stat-label">{label}</div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="#f5a623"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4.98 3.5a2.5 2.5 0 11-.02 5 2.5 2.5 0 01.02-5zM3 8.98h4v12H3v-12zM9 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1v6.31h-4v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.16 1.46-2.16 2.96v5.7H9v-12z"
        fill="currentColor"
      />
    </svg>
  );
}